import {
    Content2PointsWith1Image,
    Content3PointsWith1Image,
    Content3PointsWithNoImage,
    Content4PointsWithNoImage,
    ContentDescriptionWithNoImage,
    ContentSinglePointWith1Image,
    FinalSummary,
    IntroductionTitleWithGoals
} from "../pages/DQPro/components/SlideLayouts.comp";

export const CURRENT_VERSION = '1.10';

export const TIER_COLOR = {
    'Very High': '#1B6B55',   // dark teal-green
    High:        '#4A9278',   // medium teal-green
    Moderate:    '#EFA020',   // amber / warm orange
    Basic:       '#EFA020',   // amber / warm orange
    Low:         '#E04535',   // coral red
};

export const READINESS_BAND_COLORS = {
    High: '#1B6B55',
    Moderate: '#4A9278',
    Basic: '#EFA020',
    Low: '#E8730C',
};

export const CONTEXT_BAND_COLORS = {
    'Strong': '#1B6B55',
    'Adequate': '#4A9278',
    'Limited': '#EFA020',
    'Low': '#E04535',
};

export const RISK_BAND_COLORS = {
    Monitor: '#4A9278',
    Watch: '#EFA020',
    Elevated: '#E8730C',
    'Priority Concern': '#E04535',
};

export const DEPLOYMENT_ACTION_BAND_COLORS = {
'Growth': '#1B6B55',
'Active': '#4A9278',
'High risk': '#E04535',
'Low coverage': '#E8730C',
'Data weak': '#EFA020',
'Dormant': '#9CA3AF',
}   

// Participation and Data Confidence
export const PARTICIPATION_BAND_COLORS = {
    High: '#1B6B55',
    Medium: '#efa020',
    Low: '#E8730C',
    'Very Low': '#9CA3AF',
};

export const BAND_COLOR = {
    ...READINESS_BAND_COLORS,
    ...CONTEXT_BAND_COLORS,
    ...RISK_BAND_COLORS,
    ...DEPLOYMENT_ACTION_BAND_COLORS,
    ...PARTICIPATION_BAND_COLORS,

    // Participation and Data Confidence
    Medium: '#EFA020',
    'Very Low': '#9CA3AF',
};

export const TOAST_TYPES = {
    DANGER: 'danger',
    /*
     * Alias, not a new type.
     *
     * Fourteen call sites across five pages already reach for TOAST_TYPES.ERROR.
     * It never existed, so each of them passed undefined to the notification
     * store, which throws "Validation error. type option is required" — the
     * error message was never shown, and the throw took the rest of the handler
     * with it.
     *
     * Defined here rather than rewritten at every call site: adding a key cannot
     * change behaviour anywhere that was working.
     */
    ERROR: 'danger',
    WARN: 'warning',
    SUCCESS: 'success',
    INFO: 'info'
};

export const LOCAL_STORAGE_KEYS = {
    auth_token: 'dq_seal_cl_auth_token',
    auth_user: 'dq_seal_cl_auth_user',
    refresh_token: 'dq_seal_cl_refresh_token',
    user_email: 'dq_seal_cl_user_email',
    e2ee_krypto_pair_obj: 'dq_common_e2ee_krypto_objp',
    e2ee_krypto_disable_flg: 'dq_common_e2ee_disable_flg',
    qa_mode: 'dq_qa_mode',
    dq_insider_profile: 'dq_insider_profile',
    dq_pro_chat_history: 'dq_pro_chat_history',
    dqpro_user_message: 'dqpro_user_message',
    dqpro_v2_google_auth_redirect: 'dqpro_v2_google_auth_redirect',
    dqpro_v2_use_template: 'dqpro_v2_use_template',

    // CPC Index Government Dashboard — its own session keys, deliberately.
    // A government reviewer and an Insider user are different accounts on
    // different portals; sharing `auth_token` would mean logging into one
    // silently signs you out of the other.
    cpc_gov_auth_token: 'cpc_gov_auth_token',
    cpc_gov_refresh_token: 'cpc_gov_refresh_token',
    cpc_gov_auth_user: 'cpc_gov_auth_user',
}

export const DEFAULT_PASS_FOR_LICENSE = 'pass123';
export const DEFAULT_PASS_FOR_DASHBOARD = 'p@ssw0rd';

export const USER_TYPE = {
    REGULAR: 10,
    SUPER_USER: 1,
    ADMIN_PORTAL: 2,
    ANALYTICS_PORTAL: 3,
    DQSEAL_CLIENT_PORTAL: 4,
    DQSEAL_BO_PORTAL: 5,
    DQ_INSIDER_PORTAL: 6,
};

export const INSIDER_PROFILE_TYPE = {
    EDUCATOR: 'EDUCATOR',
    BUSINESS: 'BUSINESS',
    GUEST: 'GUEST',
}


export const USER_ACCESS_LEVEL = {
    NOT_APPLICABLE: 0,
    NO_ACCESS: 1,
    PARTIAL_ACCESS_ADMIN_PORTAL: 2,
    FULL_ACCESS_ADMIN_PORTAL: 3,
    FULL_ACCESS_ANALYTICS_PORTAL: 4,
    PARTIAL_ACCESS_ANALYTICS_PORTAL: 5,
    FULL_ACCESS_ANALYTICS_PORTAL_SINGLE_INSTITUTE: 6,
    ANALYTICS_PORTAL_PARENT_ACCOUNT: 60,
    FULL_ACCESS_DQSEAL_CLIENT_PORTAL: 7,
    PARTIAL_ACCESS_DQSEAL_CLIENT_PORTAL: 8,
    FULL_ACCESS_DQSEAL_BO_PORTAL: 9,
    PARTIAL_ACCESS_DQSEAL_BO_PORTAL: 10,
    FULL_ACCESS_DQ_INSIDER_PORTAL: 20,
    FULL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE: 21,
    PARTIAL_ACCESS_DQ_INSIDER_PORTAL_SINGLE_INSTITUTE: 22,
    // LC Teacher — created by a school, scoped to the classes bound on their
    // workspace membership. Deliberately NOT 22: a level-22 teacher would inherit
    // the school lead's whole surface (client list, licence assignment, CSV
    // upload) and every one of those would need a role check bolted on. 23 is
    // fail-closed — see the allowlist in PrivateRouteWithLayout.
    TEACHER_ACCESS_DQ_INSIDER_PORTAL: 23,
    // Individual learner — personal AI-Digital Safety dashboard at
    // /dq_pro/individual-dashboard/*. Auto-created on first assessment completion.
    INDIVIDUAL_ACCESS_DQ_INSIDER_PORTAL: 25,
};

/**
 * The role a user holds in the workspace they are CURRENTLY acting in.
 *
 * Mirrors WORKSPACE_ROLE in my-dq-api/src/utils/constants.js, and arrives on
 * the session as `workspace_role` (whitelisted in the User model's toJSON).
 *
 * It exists because access_level alone cannot separate two different roles that
 * share level 21: a partner on a single-institute plan, and a school NETWORK's
 * contact — who is a CLIENT of that partner's workspace and shares its
 * dq_insider_id. Reading the level alone reads the network as the partner.
 */
export const WORKSPACE_ROLE = {
    OWNER:      'owner',
    PEER:       'peer',
    CLIENT:     'client',
    TEACHER:    'teacher',
    INDIVIDUAL: 'individual',
};

export const USER_TYPE_VERBOSE = {
    10: 'REGULAR',
    1: 'SUPER_USER',
    2: 'ADMIN_PORTAL',
    3: 'ORG_PORTAL'
};

export const USER_ACCESS_LEVEL_VERBOSE = {
    0: 'N/A',
    1: 'No Access',
    2: 'Partial Access (Admin)',
    3: 'Full Access (Admin)',
    4: 'Full Access (Analytics - Full Org)',
    5: 'Partial Access (Analytics)',
    6: 'Full Access (Analytics - Single Inst)',
    22: 'School / Client',
    23: 'LC Teacher',
    25: 'Individual Learner',
};

export const ACL_ENABLE_ENDPOINTS = []
export const URL_PERMS_MAP = {}


export const SEAL_TYPE = {
    ALIGN: 0,
    ENHANCE: 1,
    CERTIFY: 2
}

export const SEAL_TYPE_VERBOSE = {
    [SEAL_TYPE.ALIGN]: 'Aligned',
    [SEAL_TYPE.ENHANCE]: 'Enhanced',
    [SEAL_TYPE.CERTIFY]: 'Measured'
}

export const APP_STATUS = {
    DRAFT: 0,
    WITHDRAWN: 1,
    REJECTED: 2,
    PENDING_PAYMENT: 10,
    PENDING_APPROVAL: 20,
    IN_PROGRESS_COLLECT_DATA: 30,
    HALTED_COLLECT_DATA: 40,
    COLLECTED_DATA: 45,
    IN_PROGRESS_PROCESS_DATA: 50,
    HALTED_PROCESS_DATA: 60,
    PENDING_CLEANING: 65,
    IN_PROGRESS_LEVEL_MAPPING: 67,
    PENDING_REVIEW_MAPPING: 70,
    PENDING_ANALYSIS: 75,
    PENDING_REVIEW_REPORT: 80,
    HALTED_REPORT: 90,
    COMPLETED: 100,

    READY_TO_CERTIFY: 105,
    ADD_ASSESS_REQ_RECEIVED: 110,
    REQ_REVIEWED_ON_HOLD: 115,
    REQ_REVIEWED_REJECTED: 116,
    REQ_REVIEWED_PAYMENT_PENDING: 120,
    PAYMENT_RECEIVED_VER_PENDING: 130, // assessments & licenses cost.
    PAYMENT_DECLINE_ON_HOLD: 135,
    PAYMENT_DECLINE_REJECTED: 136,
    PAYMENT_CONFIRMED: 138,
    PENDING_AUTO_ASSESS_GENERATION: 140,
    AUTO_ASSESS_GENERATION_IN_PROGRESS: 145,
    AUTO_ASSESS_GENERATION_HALTED: 150,
    AUTO_ASSESS_GENERATION_COMPLETED: 160, // in-progress review assessments
    PENDING_ASSESS_GENERATED_APPROVE: 162,
    PENDING_APPROVE_MODULES: 165,
    PENDING_PUBLISH_MODULES: 167,
    ASSESSMENTS_READY: 200,
}

export const APP_STATUS_VERBOSE = {
    [APP_STATUS.DRAFT]: 'Application under Review',
    [APP_STATUS.WITHDRAWN]: 'Withdrawn',
    [APP_STATUS.REJECTED]: 'Rejected',
    [APP_STATUS.PENDING_PAYMENT]: 'Pending Payment',
    [APP_STATUS.PENDING_APPROVAL]: 'Pending Payment Verification',
    [APP_STATUS.IN_PROGRESS_COLLECT_DATA]: 'Content to be Uploaded',
    [APP_STATUS.HALTED_COLLECT_DATA]: 'Halted',
    [APP_STATUS.COLLECTED_DATA]: 'Processing Content',
    [APP_STATUS.IN_PROGRESS_PROCESS_DATA]: 'Processing Content',
    [APP_STATUS.HALTED_PROCESS_DATA]: 'Halted',
    [APP_STATUS.PENDING_CLEANING]: 'Processing Content',
    [APP_STATUS.IN_PROGRESS_LEVEL_MAPPING]: 'Processing Content',
    [APP_STATUS.PENDING_REVIEW_MAPPING]: 'Processing Content',
    [APP_STATUS.PENDING_ANALYSIS]: 'Analyzing Content',
    [APP_STATUS.PENDING_REVIEW_REPORT]: 'Analyzing Content',
    [APP_STATUS.HALTED_REPORT]: 'Halted',
    [APP_STATUS.COMPLETED]: 'Report Available',

    [APP_STATUS.READY_TO_CERTIFY]: 'Ready to Certify',
    [APP_STATUS.ADD_ASSESS_REQ_RECEIVED]: 'Application under Review',
    [APP_STATUS.REQ_REVIEWED_ON_HOLD]: 'Impact Measure: In-Progress',
    [APP_STATUS.REQ_REVIEWED_REJECTED]: 'Impact Measure: In-Progress',
    [APP_STATUS.REQ_REVIEWED_PAYMENT_PENDING]: 'Impact Measure: In-Progress',
    [APP_STATUS.PAYMENT_RECEIVED_VER_PENDING]: 'Impact Measure: In-Progress',
    [APP_STATUS.PAYMENT_DECLINE_ON_HOLD]: 'Impact Measure: In-Progress',
    [APP_STATUS.PAYMENT_DECLINE_REJECTED]: 'Impact Measure: In-Progress',
    [APP_STATUS.PAYMENT_CONFIRMED]: 'Impact Measure: In-Progress',
    [APP_STATUS.PENDING_AUTO_ASSESS_GENERATION]: 'Processing Impact Measure',
    [APP_STATUS.AUTO_ASSESS_GENERATION_IN_PROGRESS]: 'Processing Impact Measure',
    [APP_STATUS.AUTO_ASSESS_GENERATION_HALTED]: 'Certify: In-Progress',
    [APP_STATUS.AUTO_ASSESS_GENERATION_COMPLETED]: 'Processing Impact Measure',
    [APP_STATUS.PENDING_ASSESS_GENERATED_APPROVE]: 'Processing Impact Measure',
    [APP_STATUS.PENDING_APPROVE_MODULES]: 'Processing Impact Measure',
    [APP_STATUS.PENDING_PUBLISH_MODULES]: 'Processing Impact Measure',
    [APP_STATUS.ASSESSMENTS_READY]: 'Assessment Available',
}

export const ANALYSIS_TYPES = {
    LEVEL_9_COVERAGE: 'Level_9_Coverage',
    LEVEL_8_COVERAGE: 'Level_8_Coverage',
    LEVEL_7_COVERAGE: 'Level_7_Coverage',
    LEVEL_7_ALIGNMENT: 'Level_7_Alignment',
    LEVEL_6_DEPTH: 'Level_6_Depth',
    LEVEL_6_BREADTH: 'Level_6_Breadth',
    LEVEL_6_ENOUGH: 'Level_6_Enough',
    LEVEL_5_ANALYSIS: 'Level_5_Analysis',
}

export const PAYMENT_STATUS = {
    // PENDING: 0,
    INITIALIZED: 1,
    PROCESSING: 2,
    SUCCESSFUL: 3,
    FAILED: 4,
    REFUNDED: 5
}

// export const DQ_DEFINITIONS = {
//     "DQ1": {
//         title: "Digital Citizen Identity",
//         definition: "Building and managing a healthy identity as a digital citizen with integrity.",
//         color: "#5ABAE4"
//     },
//     "DQ2": {
//         title: "Balanced Use of Technology",
//         definition: "Managing one’s life both online and offline in a balanced way by exercising self-control to regulate screen time, multitasking, and one’s engagement with digital media and devices.",
//         color: "#809BD0"
//     },
//     "DQ3": {
//         title: "Conduct Cyber-Risk Management",
//         definition: "Identifying, mitigating, and managing cyber-risks (e.g., cyberbullying, harassment, and stalking) that relate to personal online behaviors.",
//         color: "#DF67A0"
//     },
//     "DQ4": {
//         title: "Personal Cyber Security Management",
//         definition: "Detecting cyber threats (e.g., hacking, scams, and malware) against personal data and device, and using suitable security strategies and protection tools.",
//         color: "#D70F76"
//     },
//     "DQ5": {
//         title: "Digital Empathy",
//         definition: "Being aware of, being sensitive to, and being supportive of one’s own and other’s feelings, needs and concerns online.",
//         color: "#E58744"
//     },
//     "DQ6": {
//         title: "Digital Footprint Management",
//         definition: "Understanding the nature of digital footprints and their real-life consequences, managing them responsibly, and actively building a positive digital reputation.",
//         color: "#F3C64C"
//     },
//     "DQ7": {
//         title: "Media and Information Literacy",
//         definition: "Finding, organizing, analyzing, and evaluating media and information with critical reasoning.",
//         color: "#D2DC1B"
//     },
//     "DQ8": {
//         title: "Privacy Management",
//         definition: "Handling with discretion all personal information shared online to protect one’s and others’ privacy.",
//         color: "#E5DC00"
//     },
//     "DQ9": {
//         title: "Digital Co-Creator Identity",
//         definition: "Identifying and developing oneself as a co-creator of the digital ecosystem.",
//         color: "#9ED3E9"
//     },
//     "DQ10": {
//         title: "Healthy Use of Technology",
//         definition: "Understanding the benefits and harms of technology on one’s mental and physical health and managing technology use while prioritizing health and wellbeing.",
//         color: "#8EA4D5"
//     },
//     "DQ11": {
//         title: "Contact Cyber-Risk Management",
//         definition: "Identifying, mitigating, and managing risky contact online (e.g., unwanted sexual contact, offline meetings, sexual exploitation).",
//         color: "#E491BA"
//     },
//     "DQ12": {
//         title: "Network Security Management",
//         definition: "Detecting, avoiding, and managing cyber threats to cloud-based collaborative digital environments.",
//         color: "#DE2A92"
//     },
//     "DQ13": {
//         title: "Emotional Regulation and Relationship Management",
//         definition: "Recognizing and managing how one’s value system and digital competencies fits with one’s digital life. Skillfully managing one’s online relationships through cooperation, conflict management, and persuasion",
//         color: "#EA9E65"
//     },
//     "DQ14": {
//         title: "Online Collaboration",
//         definition: "The ability to establish clear and effective modes of communication that would allow expression through technologies to collaborate collectively and to achieve intended goals.",
//         color: "#F5CF7A"
//     },
//     "DQ15": {
//         title: "Computational, Data, and AI Literacy",
//         definition: "Synthesizing, creating, and producing information, media, and technology in an innovative and creative manner. Generating, processing, analyzing, presenting meaningful information from data and developing, using, and applying artificial intelligence (AI) and related algorithmic tools and strategies in order to guide informed, optimized, and contextually relevant decision-making processes.",
//         color: "#DEE45D"
//     },
//     "DQ16": {
//         title: "Intellectual Property Rights Management",
//         definition: "Understanding and managing intellectual property rights (e.g., copyrights, trademarks, and patents) when using and creating content and technology.",
//         color: "#EDE300"
//     },
//     "DQ17": {
//         title: "Digital Changemaker Identity",
//         definition: "Identifying and developing oneself as a competent and influential changemaker in the digital economy.",
//         color: "#B8E7F6"
//     },
//     "DQ18": {
//         title: "Commercial and Community Use of Technology",
//         definition: "Engaging in commercial, civic and/or political participation for the wellbeing and growth of local, national, and global communities using technology.",
//         color: "#ACBFE5"
//     },
//     "DQ19": {
//         title: "Contract Cyber-Risk Management",
//         definition: "Identifying, mitigating, and managing contractual, commercial or community cyber-risks online, such as organizational attempts to exploit individuals financially or through ideological persuasion (e.g., embedded marketing, online propaganda, and gambling).",
//         color: "#F8B0D8"
//     },
//     "DQ20": {
//         title: "Organisational Cyber Security Management",
//         definition: "Recognizing, planning, and implementing organizational cyber security defenses.",
//         color: "#E83EA2"
//     },
//     "DQ21": {
//         title: "Digital Leadership",
//         definition: "Identifying and realizing opportunities for growth and value through effective, efficient, and acceptable use of digital technologies.",
//         color: "#F5BB8E"
//     },
//     "DQ22": {
//         title: "Public and Mass Communication",
//         definition: "Communicating with an online audience effectively to exchange messages, ideas, and opinions reflecting wider business or societal discourses.",
//         color: "#FADBA2"
//     },
//     "DQ23": {
//         title: "Emerging Technology and Innovation Literacy",
//         definition: "The ability to identify, use, and create emerging technology and opportunities to acquire innovative competencies to improve professional life and contribute to the global economy.",
//         color: "#E0DE84"
//     },
//     "DQ24": {
//         title: "Participatory Rights Management",
//         definition: "Understanding and exercising one’s powers and right to online participation (e.g., their rights to personal data protection, freedom of expression, or to be forgotten), while respecting the rights of other online users.",
//         color: "#F5E34B"
//     },
//     "DQ25": {
//         title: "Digital User Identity",
//         definition: "The ability to have their identity as a digital users - having basic understanding on internet and digital media.",
//         color: "#5ABAE4"
//     },
//     "DQ26": {
//         title: "Active Use of Technology",
//         definition: "The ability to actively use various types of digital media and technology.",
//         color: "#718CC8"
//     },
//     "DQ27": {
//         title: "Content Cyber-Risk Management",
//         definition: "Identifying, mitigating, and managing risky content (e.g., harmful user-generated content, racist/hateful content, image-based abuse).",
//         color: "#D9448E"
//     },
//     "DQ28": {
//         title: "Personal Device Security Management",
//         definition: "Ability to use strategies efficiently to protect personal digital devices.",
//         color: "#CB0F64"
//     },
//     "DQ29": {
//         title: "Emotional Awareness",
//         definition: "The ability to critically recognise, evaluate, and express one’s emotions in digital environment.",
//         color: "#DB6025"
//     },
//     "DQ30": {
//         title: "Online Communication",
//         definition: "The ability to use technology effectively to communicate with others online.",
//         color: "#EFB33C"
//     },
//     "DQ31": {
//         title: "ICT Literacy",
//         definition: "The ability to use efficiently the elementary functions of information and communication technologies (essentially word/image/data processing, Internet and e-mail).",
//         color: "#C0D500"
//     },
//     "DQ32": {
//         title: "Digital Inclusion Rights",
//         definition: "The ability to understand their rights to have quality digital access across sectors, SES, and genders.",
//         color: "#CEC300"
//     },
// };

export const ASSESSMENT_REQ_TYPES = {
    PRE_SELECT_PRE_POST: 1,
    PRE_SELECT_POST_ONLY: 2,
    STANDARD_DCT: 3,
    STANDARD_DPT: 4,
    CUSTOM_PRE_POST: 5,
    CUSTOM_POST_ONLY: 6,
}

export const LANGUAGES_SUPPORTED = {
    'en-US': 'English',
    'es-ES': 'Español',
    'ja-JP': 'Japanese',
    'ko-KR': 'Korean',
    'th-TH': 'Thai',
    'vi-VN': 'Vietnamese',
};

/**
 * "Schema for Data Collection" §5.1 — primary role / activity pick-list.
 * Used by `primary_activities` (partner, network) and `institute_primary_activities`
 * (school). Multi-select; codes are sent as-is, not validated server-side yet.
 */
export const PRIMARY_ACTIVITY_CODE = {
    EDUCATION_TRAINING_PROVIDER: 'education_training_provider',
    CONSULTANCY_ADVISORY: 'consultancy_advisory',
    TECHNOLOGY_PLATFORM_PROVIDER: 'technology_platform_provider',
    RESEARCH_ACADEMIC: 'research_academic',
    GOVERNMENT_POLICY: 'government_policy',
    SCHOOL_DELIVERY: 'school_delivery',
    PROGRAM_PROVIDER: 'program_provider',
    FUNDER_SPONSOR: 'funder_sponsor',
    OTHER: 'other',
}

export const PRIMARY_ACTIVITY_CODE_VERBOSE = {
    [PRIMARY_ACTIVITY_CODE.EDUCATION_TRAINING_PROVIDER]: 'Education or training provider',
    [PRIMARY_ACTIVITY_CODE.CONSULTANCY_ADVISORY]:        'Consultancy or advisory',
    [PRIMARY_ACTIVITY_CODE.TECHNOLOGY_PLATFORM_PROVIDER]:'Technology or platform provider',
    [PRIMARY_ACTIVITY_CODE.RESEARCH_ACADEMIC]:           'Research or academic',
    [PRIMARY_ACTIVITY_CODE.GOVERNMENT_POLICY]:           'Government or policy',
    [PRIMARY_ACTIVITY_CODE.SCHOOL_DELIVERY]:             'School delivery',
    [PRIMARY_ACTIVITY_CODE.PROGRAM_PROVIDER]:            'Program provider',
    [PRIMARY_ACTIVITY_CODE.FUNDER_SPONSOR]:              'Funder or sponsor',
    [PRIMARY_ACTIVITY_CODE.OTHER]:                       'Other',
};

/**
 * "Schema for Data Collection" §5.2 — additional capabilities pick-list.
 * Used by `additional_capabilities` (network) and `institute_additional_capabilities`
 * (school). Multi-select, recorded only — does not change pricing, access or billing.
 */
export const ADDITIONAL_CAPABILITY_CODE = {
    PLATFORM_MODE_API_SSO:                 'platform_mode_api_sso',
    LMS_INTEGRATION_CONNECTOR:             'lms_integration_connector',
    ASSESSMENT_ONLY_LOCALIZATION:          'assessment_only_localization',
    FULL_PLATFORM_LOCALIZATION:            'full_platform_localization',
    RESEARCHER_REVIEW_FRAMEWORK_ALIGNMENT: 'researcher_review_framework_alignment',
    NONE:                                  'none',
};

export const ADDITIONAL_CAPABILITY_CODE_VERBOSE = {
    [ADDITIONAL_CAPABILITY_CODE.PLATFORM_MODE_API_SSO]:                 'Platform Mode, API and SSO',
    [ADDITIONAL_CAPABILITY_CODE.LMS_INTEGRATION_CONNECTOR]:             'LMS integration connector',
    [ADDITIONAL_CAPABILITY_CODE.ASSESSMENT_ONLY_LOCALIZATION]:          'Assessment-only localization',
    [ADDITIONAL_CAPABILITY_CODE.FULL_PLATFORM_LOCALIZATION]:            'Full platform localization',
    [ADDITIONAL_CAPABILITY_CODE.RESEARCHER_REVIEW_FRAMEWORK_ALIGNMENT]: 'Researcher review and framework alignment',
    [ADDITIONAL_CAPABILITY_CODE.NONE]:                                  'None',
};

/**
 * "Schema for Data Collection" §5.3 — school organization type pick-list.
 * Used by `institute_type` (school). Single select — NOT the same as the
 * numeric `org_type` already on the row. The doc's label is "Other (specify)"
 * but defines no field for the specified text, so none was added here either.
 */
export const ORGANIZATION_TYPE_CODE = {
    PRIVATE_COMPANY:             'private_company',
    NONPROFIT_NGO_FOUNDATION:    'nonprofit_ngo_foundation',
    GOVERNMENT_PUBLIC_SECTOR:    'government_public_sector',
    SCHOOL_OR_SCHOOL_NETWORK:    'school_or_school_network',
    UNIVERSITY_HIGHER_EDUCATION: 'university_higher_education',
    INTERNATIONAL_MULTILATERAL:  'international_multilateral',
    INDEPENDENT_PROFESSIONAL:    'independent_professional',
    OTHER:                       'other',
};

export const ORGANIZATION_TYPE_CODE_VERBOSE = {
    [ORGANIZATION_TYPE_CODE.PRIVATE_COMPANY]:             'Private company / business',
    [ORGANIZATION_TYPE_CODE.NONPROFIT_NGO_FOUNDATION]:    'Nonprofit, NGO or foundation',
    [ORGANIZATION_TYPE_CODE.GOVERNMENT_PUBLIC_SECTOR]:    'Government or public sector agency',
    [ORGANIZATION_TYPE_CODE.SCHOOL_OR_SCHOOL_NETWORK]:    'School or school network',
    [ORGANIZATION_TYPE_CODE.UNIVERSITY_HIGHER_EDUCATION]: 'University or higher education institution',
    [ORGANIZATION_TYPE_CODE.INTERNATIONAL_MULTILATERAL]:  'International or multilateral organization',
    [ORGANIZATION_TYPE_CODE.INDEPENDENT_PROFESSIONAL]:    'Independent professional or sole practitioner',
    [ORGANIZATION_TYPE_CODE.OTHER]:                       'Other',
};

export const CHAT_TYPE = {
    BOT: 1,
    USER: 2
};

/**
 * What a builder can do when the pool paying for a build is short — `payer.action`
 * on the API's cost and shortfall cards (PAYER_ACTION in the API's constants).
 * Only the workspace owner can buy.
 */
export const PAYER_ACTION = {
    BUY_CREDITS:        'buy_credits',        // owner of a legacy workspace: card credit top-up
    BUY_DIRECT_CREDITS: 'buy_direct_credits', // owner of a direct school or network: credit checkout
    BUY_DQT:            'buy_dqt',            // owner of a partner workspace: DQ Token top-up
    ASK_OWNER:          'ask_owner',          // anyone else in a workspace that pays itself
    ASK_PARTNER:        'ask_partner',        // a network or school drawing a partner-assigned pool
};

/** The smallest card credit top-up the API accepts (CREDIT_TOPUP_MIN), in credits = US$. */
export const CREDIT_TOPUP_MIN = 10;

/**
 * Learning program publish status — a numeric enum, not a boolean/sequential flag.
 * Only compare against these named constants (e.g. `status === LEARNING_PROGRAM_STATUS.PUBLISHED`);
 * other values may be introduced later for in-between states (e.g. "In Review").
 */
/**
 * The two publication states shared by everything that goes through a
 * draft to published gate. Defined once and spread into the enums below so the
 * numbers cannot drift apart.
 *
 * MIRRORS PUBLISH_STATUS in the API's utils/constants.js. The API renumbered
 * these from DRAFT: 0 / PUBLISHED: 5 on 2026-09-03 (commit 059a09fa) and
 * migrated stored documents with scripts/sync_draft_published_status.js. The
 * values sit above every pre-existing status (the highest was DECLINED: 9) so
 * the two vocabularies share one numeric space without colliding, and are
 * spaced 15 apart to leave room for in-between states such as IN_REVIEW.
 *
 * Do not change these without the API changing in the same commit: the number
 * is what the API stores, and this file is what reads it back.
 */
export const PUBLISH_STATUS = {
    DRAFT:     10,
    PUBLISHED: 25,
};

export const LEARNING_PROGRAM_STATUS = {
    ...PUBLISH_STATUS,
};

// Account publication lifecycle — a school / network workspace, and later a
// class, is created as a DRAFT: editable, invisible to its users, no activation
// email, nothing billed. Publishing is the explicit act that makes it real.
//
// Numeric and spaced like LEARNING_PROGRAM_STATUS so intermediate states can be
// added without renumbering. Mirrors ACCOUNT_LIFECYCLE_STATUS in the API's
// utils/constants.js — the two must not drift.
//
// NOT the same thing as LEARNING_PROGRAM_STATUS above, which is a *program's*
// own Draft → Published lifecycle. Keep them apart.
export const ACCOUNT_LIFECYCLE_STATUS = {
    DRAFT:     0,
    PUBLISHED: 5,
    EXPIRED:   8,
    ARCHIVED:  9,
};

export const ACCOUNT_LIFECYCLE_STATUS_VERBOSE = {
    0: 'Draft',
    5: 'Published',
    8: 'Expired',
    9: 'Archived',
};

// How parental consent is obtained for an account's learners, and who opens
// their learning space. Chosen once by the partner at publish, and effectively
// immutable afterwards — the pack: "The pathway can't be changed afterwards
// without re-adding learners."
//
// Mirrors ACCOUNT_CONSENT_PATHWAY in the API's utils/constants.js. 0 is unused
// so "not chosen" stays distinguishable from option 1.
export const ACCOUNT_CONSENT_PATHWAY = {
    SCHOOL_COLLECTS:          1,
    PARENT_CONSENTS_BY_EMAIL: 2,
    PARENT_OPENS_SPACE:       3,
};

// School → owner learning-program request lifecycle. Spaced like
// LEARNING_PROGRAM_STATUS so states can be inserted later without renumbering.
// Decline and cancel are the same outcome, so there is a single DECLINED state.
// Mirrors WORKSPACE_REQUEST_STATUS in the API model. CHANGES_REQUESTED and
// ACCEPTED serve the partner-initiated suggestion loop, where the school answers
// before any build starts; a school-raised request never enters either.
export const WORKSPACE_REQUEST_STATUS = {
    PENDING:           0,
    CHANGES_REQUESTED: 1, // suggestion only — school asked for adjustments
    IN_PROGRESS:       2, // owner started building — a program exists but isn't published
    ACCEPTED:          3, // suggestion only — school accepted, not yet started
    BUILT:             5, // owner delivered a published program
    DECLINED:          9,
    /*
     * Written by the DATAHOUSE, never by the API: a suggestion drafted from
     * assessment results and parked for the partner to review. The partner
     * pressing "Suggest Program" moves it to PENDING, which is where the
     * school first sees it.
     */
    READY_TO_SUGGEST:  10,
    // Shared with LEARNING_PROGRAM_STATUS - same numbers, same meaning, so a
    // request status and a program status can be compared or carried in one
    // field. Nothing writes these yet; the conversation states above are still
    // what the request lifecycle moves through.
    ...PUBLISH_STATUS,
};

/**
 * A ProfileChangeRequest — a learner correcting their own details at first
 * sign-in, for their school to approve.
 *
 * MIRRORS PROFILE_CHANGE_STATUS in the API's utils/constants.js. Numbered and
 * spaced like WORKSPACE_REQUEST_STATUS above so a state can be added later
 * without renumbering stored documents.
 */
export const PROFILE_CHANGE_STATUS = {
    PENDING:  0,
    APPROVED: 5,
    REJECTED: 9,
};

export const PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT = {
    VIDEO: 'Video',
    LEFT_IMAGE_SLIDE: 'LeftImageSlide',
    RIGHT_IMAGE_SLIDE: 'RightImageSlide',
    OPTIONS_SLIDE: 'OptionsSlide',
    STEPS_SLIDE: 'StepsSlide',
    SUMMARY_SLIDE: 'SummarySlide',
    TEXT_SLIDE: 'TextSlide',
    CHAT_SLIDE: 'ChatSlide',
    // New Layouts
    INTRO_TITLE_WITH_GOALS: "Introduction_title_with_goals",
    CONTENT_3_POINTS_WITH_NO_IMAGE: "content_3_points_with_no_image",
    CONTENT_4_POINTS_WITH_NO_IMAGE: "content_4_points_with_no_image",
    CONTENT_2_POINTS_WITH_1_IMAGE: "content_2_points_with_1_image",
    CONTENT_3_POINTS_WITH_1_IMAGE: "content_3_points_with_1_image",
    CONTENT_SINGLE_POINT_WITH_1_IMAGE: "content_single_point_with_1_image",
    CONTENT_DESCRIPTION_WITH_NO_IMAGE: "content_description_with_no_image",
    FINAL_SUMMARY: "final_summary"
}


export const PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT_VERBOSE = {
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.INTRO_TITLE_WITH_GOALS]: IntroductionTitleWithGoals,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_3_POINTS_WITH_NO_IMAGE]: Content3PointsWithNoImage,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_4_POINTS_WITH_NO_IMAGE]: Content4PointsWithNoImage,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_2_POINTS_WITH_1_IMAGE]: Content2PointsWith1Image,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_3_POINTS_WITH_1_IMAGE]: Content3PointsWith1Image,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_SINGLE_POINT_WITH_1_IMAGE]: ContentSinglePointWith1Image,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.CONTENT_DESCRIPTION_WITH_NO_IMAGE]: ContentDescriptionWithNoImage,
    [PBDQ3_PROGRAM_SESSION_CONTENT_LAYOUT.FINAL_SUMMARY]: FinalSummary,
};

export const KSAB_COLOR_MAPPING = {
    "Knowledge": "#63B8B1",
    "Skills": "#FF3E89",
    "Attitudes": "#FFB198",
    "Behaviours": "#F8B62D"
};

export const DQC_COLORS = {
    "DQC01": "#5ABAE4",
    "DQC02": "#809BD0",
    "DQC03": "#DF67A0",
    "DQC04": "#D70F76",
    "DQC05": "#E58744",
    "DQC06": "#F3C64C",
    "DQC07": "#D2DC1B",
    "DQC08": "#E5DC00",
    "DQC09": "#9ED3E9",
    "DQC10": "#8EA4D5",
    "DQC11": "#E491BA",
    "DQC12": "#DE2A92",
    "DQC13": "#EA9E65",
    "DQC14": "#F5CF7A",
    "DQC15": "#DEE45D",
    "DQC16": "#EDE300",
    "DQC17": "#B8E7F6",
    "DQC18": "#ACBFE5",
    "DQC19": "#F8B0D8",
    "DQC20": "#E83EA2",
    "DQC21": "#F5BB8E",
    "DQC22": "#FADBA2",
    "DQC23": "#E0DE84",
    "DQC24": "#F5E34B",
    "DQC25": "#5ABAE4",
    "DQC26": "#718CC8",
    "DQC27": "#D9448E",
    "DQC28": "#CB0F64",
    "DQC29": "#DB6025",
    "DQC30": "#EFB33C",
    "DQC31": "#C0D500",
    "DQC32": "#CEC300"
};

// Feature flag: if true, enables preview when first session (M1_U1_S1) is done AND progress >= 50%
// If false, requires 100% completion of all sessions
export const ENABLE_EARLY_PREVIEW = false;

// Email list for users who should see the session warning dialog when entering > 10 sessions
export const SESSION_WARNING_EMAIL_LIST = [
    'justinsplash0@gmail.com',
    'acaplan@havergal.on.ca',
    'henry.platten@gmail.com',
    'lalit@futuready.solutions',
    'wangjinglei@student.usm.my',
    'gene.limjingyi@usm.my',
    'TMorais@Cyberbullying.PT',
    'cristiane.miranda.mk@gmail.com',
    'franciscojoaquin.villenacarrillo@telefonica.com',
    'editkerekes.mailbox@gmail.com',
    'thianal@gmail.com',
    'Awo@childonlineafrica.org',
    'sherrymea.z@gmail.com',
    'sherrymea@hotmail.com',
    'jonhirst@innovationinmission.com',
    'ljlalit@gmail.com',
    'jpuga@unoi.com',
    'info@agarradosa.net',
    'jmarinelliu@gmail.com',
    'galli.flor@gmail.com',
    'Michelle.fong@claremontschool.co.uk',
    'oksana.tatarina@rigatechgirls.com',
    'john.mikton@ecolint.ch',
    'kalkan38kalkan@gmail.com',
    'info@cyber-felix.com',
    'lia.erfani.kh@gmail.com',
    'nataliconti2030@gmail.com',
    'timothy.legrand@msn.com',
    'kane.meza.1959+poweredbydq.com@gmail.com',
    'indiana.ramaciotti@msn.com',
    'dalley.kathleen@msn.com',
    'blackston.dominga@outlook.com',
    'alyssa.faulding@googlemail.com',
    'elba.graves@gmail.com',
    'nataliconti2030@gmail.com',
    'worthen.lanora@yahoo.com',
    'info@hawken.medicopostura.com',
    'aliza.rushing@gmail.com',
    'bess.ridgeway@gmail.com',
    'toups.avis@gmail.com',
    'huondekerilleau.arlen18@yahoo.com',
    'kurtis.gipson52@googlemail.com',
    'thanh.ewing@gmail.com',
    'greta.hayter82@msn.com',
    'ute.stout@hotmail.com',
    'hortense.howell60@gmail.com',
    'gainer.valentin@hotmail.com',
    'emma.wilson1768@gmail.com',
    'gemmamarshall112@gmail.com',
    'angela.marzo@movingtoaction.ca',
    'margarita.araque@gmail.com',
    'deediao@gmail.com',
    'ilona.witczak@gmail.com',
    'mila@kigumigroup.com',
    'villani@evolve-edu.com.au',
    'gilbert.lambie7@gmail.com',
    'micaela.jaynes@gmail.com',
    'outreachseo56@gmail.com',
    'twila.darbyshire@googlemail.com',
    'hebert.lora@outlook.com',
    'maacruise@yahoo.com',
    'soylusimal2@gmail.com',
    'trung.nguyen@kdi.edu.vn',
    'roberto@adaptworks.com.br',
    'dean@mediaweb.co.za',
    'director@shalaedu.com',
    'gabriela.svacova123@gmail.com',
    'edueya.ai@gmail.com',
    'ninasmile8@qq.com',
    'chairat.boo@mahidol.ac.th',
    'hansol@igroupkorea.com',
    'Noluxolo.Gcaza@mandela.ac.za',
    'alexloh@nanyangeducation.com',
    'ANGELTEO@GMAIL.COM',
    'arina.irrisya@gmail.com',
    'carl.ruano.sg@gmail.com',
    'ric.chan.2024@mqf.smu.edu.sg',
    'charles.ng@tmw.com.sg',
    'cippolito@sas.edu.sg',
    'cynthiacychow@discoverymind.edu.hk',
    'eddy.chong@nie.edu.sg',
    'ceo@shalaedu.com',
    'green@eddicted.to',
    'Jasmine_de@hotmail.com',
    'ninasmile8@qq.com',
    'lkt18460@yahoo.com.sg',
    'joowon.lee@igroupkorea.com',
    'director@shalaedu.com',
    'lincolnting@gmail.com',
    'ljiangcn@gmail.com',
    'aisya2086@gmail.com',
    'Nashlin.tp@gmail.com',
    'rizal@irsyad.edu.sg',
    'robertkaryeh101@gmail.com',
    'roza@jamiyah.org.sg',
    'shibuyume++dqpro@gmail.com',
    'swetha@bricksandblockscoaching.com',
    'tklee@asiafranchise.net',
    'hey.yingling@gmail.com',
    'tatianatagle@grupoaledu.com',
    'tristanmontano@avinya.ph',
    'kalai_vani_sivam@moe.edu.sg',
    'vivienne@starvistaeducation.com.my',
    'teacheryvonne.neo@gmail.com',
    'margarita@enyeart.medicopostura.com',
    'cinda.chapple@yahoo.com',
    'nplaza@sas.edu.sg',
    'raymond@genue.ai',
    'y-mita@tokyo-shoseki.co.jp',
    'kiyotou@tokyo-shoseki.co.jp',
    'eric.eugenio@cenexus.sg',
    'keith.tay@jdi.group',
    'raphael.walker@accredify.io',
    'symorgankim08@gmail.com',
    'uni91946825@gmail.com',
    'usman+waitlist1@dqforall.com',
    'usman+waitlist2@dqforall.com',
    'davis+waitlist1@dqforall.com',
    'davis+waitlist2@dqforall.com',
    'lydia+waitlist1@dqforall.com',
    'lydia+waitlist2@dqforall.com'
];

/**
 * The LearnCreator landing page — where EVERY entry point to LearnCreator
 * opens (bug sheet Ian-LC-30Sep-1): Solutions > LearnCreator > Create a
 * Program, the sidebar's LearnCreator item, My Programs > + New program, and
 * the Partner Home's "Open LearnCreator". Typing a goal there starts the
 * builder (/dq_pro/program/creation/preview) on a fresh program.
 */
export const LEARNCREATOR_START_PATH = '/dq_pro/learncreator/start';


export const FEATURE_FLAGS = {
    // Flag: 0 = disabled, 1 = enabled
    ENABLE_BILLING_FLAG: parseInt(process.env.REACT_APP_ENABLE_BILLING_SERVICE || '0', 10) === 1,
}


// Plan keys must match the backend enum exactly
export const DQ_PRO_PLAN_KEY = {
    DEFAULT:            'default',
    // GLOBAL_STANDARDS:   'global_standards',
    // BASELINE_AND_TEACH: 'baseline_and_teach',
    DQ_PRO:             'dq_pro',
    EDUCATOR:           'educator',
    ENTERPRISE:         'enterprise',

    // enterprise plan keys
    ENTERPRISE_P0:      'enterprise_p0',
    ENTERPRISE_P1:      'enterprise_p1',
    ENTERPRISE_P2:      'enterprise_p2',
    ENTERPRISE_P3:      'enterprise_p3',
    ENTERPRISE_P4:      'enterprise_p4',

    // PbDQ solution plans. These exist in the plans collection alongside p0-p4;
    // they are the keys the PbDQ Navigation sheet tags rows with (P1A, P1B, LC).
    // Named in the Unlock dialog on PW deck slide 9.
    ENTERPRISE_P1A:     'enterprise_p1a',
    ENTERPRISE_P1B:     'enterprise_p1b',
    ENTERPRISE_LC:      'enterprise_lc',

    // Direct plans (23 Sep 2026): a school or school network buying for itself. Each
    // sells its enterprise plan's solution — see solutionPlanKey() in productAccess.js.
    // A direct plan carries its solution's variant (30 Sep 2026): direct P1 is always
    // P1B (no partner to build P1A programs); P2A and P2B are separate direct plans.
    DIRECT_P0:          'direct_p0',
    DIRECT_P1B:         'direct_p1b',
    DIRECT_LC:          'direct_lc',
    DIRECT_P2A:         'direct_p2a',
    DIRECT_P2B:         'direct_p2b',
    // Legacy keys, renamed / split by the API's split_direct_variant_plans script —
    // kept so a session signed in before it ran still resolves.
    DIRECT_P1:          'direct_p1',
    DIRECT_P2:          'direct_p2',
};

export const DQ_PRO_PLAN_KEY_VERBOSE = {
    [DQ_PRO_PLAN_KEY.DEFAULT]:            'Free',
    // [DQ_PRO_PLAN_KEY.GLOBAL_STANDARDS]:   'Global Standards',
    // [DQ_PRO_PLAN_KEY.BASELINE_AND_TEACH]: 'Baseline & Teach',
    [DQ_PRO_PLAN_KEY.DQ_PRO]:         'DQ Pro',
    [DQ_PRO_PLAN_KEY.EDUCATOR]:       'Educator Plan',
    [DQ_PRO_PLAN_KEY.ENTERPRISE]:     'Enterprise',

    // enterprise plans
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P0]:  'Child Online Safety Assessments',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1]:  'Learning Tracker',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P2]:  'Impact Validation',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P3]:  'National System',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P4]:  'Framework',

    // Wording taken from the Unlock dialog (PW deck slide 9 notes).
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1A]: 'COS Readiness Solution — Provider-Created Programs',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_P1B]: 'COS Readiness Solution — Teacher-Created Programs',
    [DQ_PRO_PLAN_KEY.ENTERPRISE_LC]:  'LearnCreator',
};

// All enterprise sub-plans (p0–p4)
export const ENTERPRISE_PLAN_KEYS = [
    DQ_PRO_PLAN_KEY.ENTERPRISE_P0,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P1,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P2,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P3,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P4,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P1A,
    DQ_PRO_PLAN_KEY.ENTERPRISE_P1B,
    DQ_PRO_PLAN_KEY.ENTERPRISE_LC,
];

export const COST_PER_USER_LEARNING = 2;
export const COST_PER_USER_ASSESSMENT = 3;

export const USER_GROUPS = {
    YOUNGPEOPLE: 'YoungPeople',
    PARENTS: 'Parents',
    TEACHERS: 'Teachers',
    SCHOOLLEADERS: 'SchoolLeaders',
    GENERAL: 'General',
}

export const REQUEST_TYPE = {
    NEW: 'new',
    RETRY: 'retry',
}

export const PRODUCT_KEY = {
    LEARNING_PROGRAM:         'learning_program',
    ASSESSMENT:               'assessment',
    CPC_INDEX_ASSESSMENT:     'cpc_index_assessment',
    STARS_ALIGNMENT:          'stars_alignment',
    FRAMEWORK_CREATOR:        'framework_creator',
    IMPACT_DASHBOARD:         'impact_dashboard',
    // Future products — just add a key here and a ProductDefinition DB record
};

// Maps a LinkTypePickerDialog card (LINK_TYPE.* in
// pages/DQPro/components/LinkTypePickerDialog.comp.jsx) to the PAC product
// key its "Assign" flow actually toggles via pac.service.js. Literal string
// keys here (not an imported LINK_TYPE) to avoid a components->constants
// reverse dependency. Only 'learn_creator' ships so far — the "Link
// Products" flow only offers that card — but more will be added as sibling
// product types (Learning Program, Standard Assessment) move onto the same
// Assign/Confirm flow.
export const LINK_TYPE_TO_PRODUCT_KEY = {
    learn_creator: PRODUCT_KEY.LEARNING_PROGRAM,
};

/**
 * NAMED ACCESS LEVELS for the Learning Program product.
 *
 * The PAC permission dimensions (view / create / link) are the storage format;
 * these are the three combinations the product actually sells, each with a name
 * people use out loud. Assigning access means picking one of these, never
 * hand-assembling a permission object at a call site.
 *
 *   LearnCreator  view + link + create  — builds its own programs, and
 *                                         distributes them
 *   LearnLinker   view + link           — distributes programs somebody else
 *                                         authored; does not author
 *   LearnViewer   view                  — sees programs, distributes nothing
 *
 * LearnLinker is what a school or network is SEEDED with at creation, and what
 * linking a program grants — "the client distributes the program, it does not
 * author programs". LearnCreator is what "Link Products → Assign LearnCreator"
 * hands over, and it is the only one of the three that opens authoring.
 *
 * `learn_creator` is deliberately the same string as LINK_TYPE.LEARN_CREATOR:
 * the card in the picker and the access level it assigns are one idea.
 */
export const LEARNING_PROGRAM_ACCESS = {
    LEARN_CREATOR: 'learn_creator',
    LEARN_LINKER:  'learn_linker',
    LEARN_VIEWER:  'learn_viewer',
};

/** The PAC permission set behind each named access level. */
export const LEARNING_PROGRAM_ACCESS_PERMISSIONS = {
    [LEARNING_PROGRAM_ACCESS.LEARN_CREATOR]: { view: true, create: true,  link: true  },
    [LEARNING_PROGRAM_ACCESS.LEARN_LINKER]:  { view: true, create: false, link: true  },
    [LEARNING_PROGRAM_ACCESS.LEARN_VIEWER]:  { view: true, create: false, link: false },
};

/**
 * What each "Link Products" card assigns — the sibling of
 * LINK_TYPE_TO_PRODUCT_KEY above: that map says WHICH product the card touches,
 * this one says WHICH access level it hands over. Literal string keys for the
 * same reason (no components -> constants reverse dependency).
 *
 * Send the permissions explicitly, always. The backend stores an org override as
 * a whole sub-document (`$set: product_access_overrides.<key>`), so a payload
 * that omits `permissions` does not "leave them as they were" — it DELETES them,
 * and the resolver then falls back to whatever the partner's own plan entry
 * happens to carry. Assigning LearnCreator that way granted `create` only by
 * accident, and granted nothing at all when the partner's plan said
 * `create: false`.
 */
export const LINK_TYPE_TO_ACCESS_PERMISSIONS = {
    learn_creator: LEARNING_PROGRAM_ACCESS_PERMISSIONS[LEARNING_PROGRAM_ACCESS.LEARN_CREATOR],
};

// PAC v3 feature keys (mirror my-dq-api FEATURE_KEY). Gated via user.feature_access.
export const FEATURE_KEY = {
    WHITE_LABEL_BRANDING: 'white_label_branding',
    API_ACCESS:           'api_access',
    LOCALIZATION:         'localization',
    ADVANCED_ANALYTICS:   'advanced_analytics',
    PLATFORM_MODE:        'platform_mode',
    SCHOOL_DASHBOARD:     'school_dashboard',     // client (enterprise P0)
    SCHOOL_NETWORK_DASHBOARD: 'school_network_dashboard', // school network (is_school_network)
    CLASS_DASHBOARD:      'class_dashboard',      // client (enterprise P0)
    NATIONAL_DASHBOARD:   'national_dashboard',
    PARTNER_DASHBOARD:    'partner_dashboard',    // owner (enterprise P0)
    INDIVIDUAL_DASHBOARD: 'individual_dashboard', // individual learner (access 25)
    PROGRAM_IMPACT_DASHBOARD: 'program_impact_dashboard', // LC Teacher (access 23 + capability)
};

// Assessment catalogue discriminator sent to the assessment builder endpoints.
// 'airy' = AI Readiness Assessment, 'cpci' = CPC Index Assessment.
export const ASSESSMENT_TYPE = {
    AIRY: 'airy',
    CPCI: 'cpci',
};

// Fixed display name + per-language descriptions used when creating a CPC Index
// Assessment. The CPCI flow does NOT ask for a name/description — these are
// applied automatically. Edit the copy here to change what learners see.
// Keys match LANGUAGES_SUPPORTED; 'en-US' is the fallback.
//
// English-only for now: only the 'en-US' entry is used until multi-language is
// enabled (see ENABLE_LANGUAGE_SELECTION). The other locales are kept ready so
// that, once enabled, each selected language picks up its own name + description.
export const CPCI_ASSESSMENT_NAMES = {
    'en-US': 'Child Online Safety Children Assessment',
    'es-ES': 'Evaluación CPCI',
    'ja-JP': 'CPCIアセスメント',
    'ko-KR': 'CPCI 평가',
    'th-TH': 'การประเมิน CPCI',
    'vi-VN': 'Đánh giá CPCI',
};
export const CPCI_ASSESSMENT_DESCRIPTIONS = {
    'en-US': 'A comprehensive assessment to check how safe you are online.',
    'es-ES': 'Una evaluación integral para comprobar qué tan seguro estás en línea.',
    'ja-JP': 'オンラインでどれだけ安全かを確認するための包括的な評価。',
    'ko-KR': '온라인에서 얼마나 안전한지 확인하기 위한 종합 평가입니다.',
    'th-TH': 'การประเมินแบบครอบคลุมเพื่อดูว่าคุณปลอดภัยบนโลกออนไลน์เพียงใด',
    'vi-VN': 'Bài đánh giá toàn diện để kiểm tra mức độ an toàn của bạn khi trực tuyến.',
};